import { getLocalTime } from '../../../helpers/forms/DateHelper/DateHelper';
import React, { useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { SharedComponent } from '../../../enums';
import useDevice from '../../useDevice';
import useTranslation from '../../useTranslation';
import DatetimeCustomEditor from '../DatetimeCustomEditor/DatetimeCustomEditor';
import DatetimeNativeEditor from '../DatetimeNativeEditor/DatetimeNativeEditor';
import type { FormFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';

/**
 * Datetime field specification. NameT is the name of the field and DataT is the definition of bean
 * that holds all the fields for the form.
 */
export interface DatetimeSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends FormFieldType<NameT, DataT, FormFieldVariant.DATETIME> {
  /**
   * Datetime field input type.
   */
  inputType?: Date;
  /**
   * Date format for date picker. Defaults to 'MMMM dd, yyyy h:mm aa' if time is selectable else to ''MMMM dd, yyyy'.
   */
  dateFormat?: string;
  /**
   * Minimum date that can be selected. Defaults to current date.
   */
  minDate?: Date;
  /**
   * Maximum date that can be selected. Defaults to infinity.
   */
  maxDate?: Date;
  /**
   * Allow time selection in date picker. Defaults to true.
   */
  useTimeSelect?: boolean;
  /**
   * Time format for date picker. Defaults to 'h:mm aa'.
   */
  timeFormat?: string;
  /**
   * Time interval in time selection in date picker. Defaults to 15 minutes.
   */
  timeInterval?: number;
  /**
   * Placeholder for date picker input field.
   */
  placeholder?: string;
  /**
   * Event listener that triggers when change happens on the field.
   *
   * @callback
   * @param date that has been changed.
   */
  onChangeHandler?: (date: Date) => void;
  /**
   * Whether to show date picker always.
   */
  showPickerAlways?: boolean;
  /**
   * If true allows date picker to select range between two dates.
   */
  useDateRange?: boolean;
  /**
   * If true displays the clear icon
   */
  useClear?: boolean;
  /**
   * If true disable the keyboard action
   */
  isKeyboardDisabled?: boolean;
  /**
   * Whether to show timezone picker or not.
   */
  showTimezonePicker?: boolean;
  /**
   * The data test id
   */
  dataTestId?: string;
}

/**
 * Form field that uses datepicker for date & time input.
 *
 * @constructor
 *
 * @author Vatsalya Agarwal
 */
const DateTimeEditorField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, DatetimeSpec<NameT, DataT>>): React.ReactElement => {
  const hasBeenFocused = useRef(false);
  const { formatMessage, loading: textLoading } = useTranslation(
    SharedComponent.DATE_TIME_EDITOR_FIELD
  );
  const { isMobile } = useDevice();
  const cx = useClassNameMapper();
  const localDate = new Date();
  const {
    dateFormat = 'MMMM dd, yyyy h:mm aa',
    minDate = new Date(),
    maxDate = new Date(localDate.setFullYear(localDate.getFullYear() + 2)),
    useTimeSelect = true,
    timeFormat = 'h:mm aa',
    timeInterval = 15,
    placeholder = formatMessage('datetime.placeholder'),
    focus,
    focusHandler,
    onChangeHandler,
    className,
    dateInputClassName,
    showPickerAlways = false,
    useDateRange = false,
    useClear = true,
    isKeyboardDisabled,
    showTimezonePicker = false,
    disabled,
    dataTestId = 'datePicker'
  } = fieldSpec;

  if (textLoading) {
    return null;
  }

  return (
    <FormField
      fieldSpec={fieldSpec}
      fieldType={FieldType.CONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(className)}
    >
      {({
        render: {
          field: { value, onChange },
          fieldState: { error }
        }
      }) => {
        return isMobile && !showPickerAlways ? (
          <DatetimeNativeEditor
            minDate={minDate}
            useTimeSelect={useTimeSelect}
            onChange={date => {
              if (onChangeHandler) {
                onChangeHandler(date);
              }
              onChange(date);
            }}
            value={value?.dateTime}
            focus={focus}
            focusHandler={focusHandler}
            hasBeenFocused={hasBeenFocused}
            placeholder={placeholder}
          />
        ) : (
          <DatetimeCustomEditor
            minDate={minDate}
            openToDate={value?.timezone ? getLocalTime(value?.timezone) : new Date()}
            maxDate={maxDate}
            disabled={disabled}
            dateFormat={dateFormat}
            useTimeSelect={useTimeSelect}
            timeFormat={timeFormat}
            timeInterval={timeInterval}
            showPickerAlways={showPickerAlways}
            placeholder={placeholder}
            onChange={date => {
              if (onChangeHandler) {
                onChangeHandler(date);
              }
              onChange(date);
            }}
            value={value}
            focus={focus}
            focusHandler={focusHandler}
            hasBeenFocused={hasBeenFocused}
            className={cx(
              {
                'w-100': showPickerAlways || useDateRange || (!showPickerAlways && !useClear),
                'w-50': !showPickerAlways && !useDateRange
              },
              dateInputClassName
            )}
            useDateRange={useDateRange}
            useClear={useClear}
            isKeyboardDisabled={isKeyboardDisabled}
            showTimezonePicker={showTimezonePicker}
            isInvalid={error ? true : false}
            dataTestId={dataTestId}
          />
        );
      }}
    </FormField>
  );
};

export default DateTimeEditorField;
